import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import RiskMatrix from "./RiskMatrix";
import RiskMatrixFMEA from "./RiskMatrixFMEA";

/**
 * Picks between the P×S (AIHA) and FMEA risk visualisations based on the
 * assessment's `matrix_type`. When `assessmentId` is provided, also renders an
 * inline selector so the user can switch matrices on the fly (and persists the
 * choice on the assessment doc).
 */
export default function RiskMatrixSwitcher({
  matrixType = "pxs",
  assessmentId = null,
  onChange,        // (newType) => void; called AFTER backend persistence succeeds
  expectedWorkers = 0,
  showSelector = false,
  editable = false,
  fmeaOverrides = {},
  strict = false,
  ...sharedProps   // overallScore, dimensions, criticalCount, totalResponses, showLegend
}) {
  const [current, setCurrent] = useState(matrixType === "fmea" ? "fmea" : "pxs");
  const [busy, setBusy] = useState(false);

  const switchTo = async (next) => {
    if (next === current) return;
    if (!assessmentId) {
      // Ephemeral switch — no persistence requested.
      setCurrent(next);
      onChange?.(next);
      return;
    }
    setBusy(true);
    try {
      await api.put(`/assessments/${assessmentId}/matrix-type`, { matrix_type: next });
      setCurrent(next);
      onChange?.(next);
      toast.success(`Matriz alterada para ${next === "fmea" ? "FMEA (S × O × D)" : "P × S (AIHA)"}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="risk-matrix-switcher">
      {showSelector && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="overline">
            {current === "fmea" ? "Matriz FMEA · S × O × D" : "Matriz P × S · AIHA"}
          </div>
          <div className="inline-flex border border-slate-300 bg-white p-1" data-testid="matrix-type-toggle">
            <button onClick={() => switchTo("pxs")} disabled={busy}
                    data-testid="matrix-type-pxs"
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                      current === "pxs" ? "bg-emerald-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              P × S
            </button>
            <button onClick={() => switchTo("fmea")} disabled={busy}
                    data-testid="matrix-type-fmea"
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                      current === "fmea" ? "bg-emerald-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              FMEA
            </button>
          </div>
        </div>
      )}

      {current === "fmea"
        ? <RiskMatrixFMEA {...sharedProps}
                          expectedWorkers={expectedWorkers}
                          assessmentId={assessmentId}
                          editable={editable}
                          initialOverrides={fmeaOverrides}
                          strict={strict} />
        : <RiskMatrix {...sharedProps} strict={strict} />}
    </div>
  );
}
